/**
 * PathNav Style Configurator
 * UI for customizing node and connection appearance
 */

import React, { useState } from 'react';
import { Button } from './ui/button';
import { defaultNodeStyle, defaultConnectionStyle, stylePresets } from '../pathnav-runtime/schemas/style-schema';

const PathNavStyleConfigurator = ({ initialNodeStyle, initialConnectionStyle, onSave, onClose }) => {
  const [nodeStyle, setNodeStyle] = useState(initialNodeStyle || defaultNodeStyle);
  const [connectionStyle, setConnectionStyle] = useState(initialConnectionStyle || defaultConnectionStyle);

  const updateNodeStyle = (key, value) => {
    setNodeStyle(prev => ({ ...prev, [key]: value }));
  };

  const updateNodeShadow = (key, value) => {
    setNodeStyle(prev => ({
      ...prev,
      shadow: { ...prev.shadow, [key]: value }
    }));
  };

  const updateConnectionStyle = (key, value) => {
    setConnectionStyle(prev => ({ ...prev, [key]: value }));
  };

  const applyPreset = (presetName) => {
    const preset = stylePresets[presetName];
    if (preset) {
      setNodeStyle(preset.node);
      setConnectionStyle(preset.connection);
    }
  };

  const handleSave = () => {
    onSave({ nodeStyle, connectionStyle });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold">🎨 PathNav Style Configurator</h2>
          <p className="text-sm text-gray-600 mt-1">Customize node and connection appearance</p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Node Style */}
            <div>
              <h3 className="text-lg font-semibold mb-4">📦 Node Appearance</h3>
              
              {/* Presets */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Quick Presets</label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => applyPreset('professional')}>Professional</Button>
                  <Button size="sm" variant="outline" onClick={() => applyPreset('playful')}>Playful</Button>
                  <Button size="sm" variant="outline" onClick={() => applyPreset('minimalist')}>Minimalist</Button>
                </div>
              </div>

              {/* Shape */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Shape</label>
                <select
                  value={nodeStyle.shape}
                  onChange={(e) => updateNodeStyle('shape', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="rectangle">Rectangle</option>
                  <option value="rounded">Rounded</option>
                  <option value="oval">Oval</option>
                  <option value="circle">Circle</option>
                  <option value="none">None (text only)</option>
                </select>
              </div>

              {/* Colors */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Background</label>
                  <input
                    type="color"
                    value={nodeStyle.backgroundColor}
                    onChange={(e) => updateNodeStyle('backgroundColor', e.target.value)}
                    className="w-full h-10 border rounded cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Text Color</label>
                  <input
                    type="color"
                    value={nodeStyle.textColor}
                    onChange={(e) => updateNodeStyle('textColor', e.target.value)}
                    className="w-full h-10 border rounded cursor-pointer"
                  />
                </div>
              </div>

              {/* Border */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Border Color</label>
                  <input
                    type="color"
                    value={nodeStyle.borderColor}
                    onChange={(e) => updateNodeStyle('borderColor', e.target.value)}
                    className="w-full h-10 border rounded cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Border Width ({nodeStyle.borderWidth}px)</label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={nodeStyle.borderWidth}
                    onChange={(e) => updateNodeStyle('borderWidth', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Border Style</label>
                <select
                  value={nodeStyle.borderStyle}
                  onChange={(e) => updateNodeStyle('borderStyle', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="solid">Solid</option>
                  <option value="dashed">Dashed</option>
                  <option value="dotted">Dotted</option>
                  <option value="none">None</option>
                </select>
              </div>

              {/* Shadow */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">✨ Shadow</label>
                  <input
                    type="checkbox"
                    checked={nodeStyle.shadow.enabled}
                    onChange={(e) => updateNodeShadow('enabled', e.target.checked)}
                    className="w-4 h-4"
                  />
                </div>
                {nodeStyle.shadow.enabled && (
                  <div className="pl-4 space-y-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Blur ({nodeStyle.shadow.blur}px)</label>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        value={nodeStyle.shadow.blur}
                        onChange={(e) => updateNodeShadow('blur', parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Offset X ({nodeStyle.shadow.offsetX}px)</label>
                        <input
                          type="range"
                          min="-10"
                          max="10"
                          value={nodeStyle.shadow.offsetX}
                          onChange={(e) => updateNodeShadow('offsetX', parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Offset Y ({nodeStyle.shadow.offsetY}px)</label>
                        <input
                          type="range"
                          min="-10"
                          max="10"
                          value={nodeStyle.shadow.offsetY}
                          onChange={(e) => updateNodeShadow('offsetY', parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Connection Style & Preview */}
            <div>
              <h3 className="text-lg font-semibold mb-4">🔗 Connection Lines</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  value={connectionStyle.type}
                  onChange={(e) => updateConnectionStyle('type', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="straight">Straight</option>
                  <option value="curved">Curved</option>
                  <option value="orthogonal">Orthogonal</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Color</label>
                  <input
                    type="color"
                    value={connectionStyle.color}
                    onChange={(e) => updateConnectionStyle('color', e.target.value)}
                    className="w-full h-10 border rounded cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Width ({connectionStyle.width}px)</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={connectionStyle.width}
                    onChange={(e) => updateConnectionStyle('width', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Style</label>
                <select
                  value={connectionStyle.style}
                  onChange={(e) => updateConnectionStyle('style', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="solid">Solid</option>
                  <option value="dashed">Dashed</option>
                  <option value="dotted">Dotted</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={connectionStyle.arrowhead}
                    onChange={(e) => updateConnectionStyle('arrowhead', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">Show Arrowhead</span>
                </label>
              </div>

              {/* Preview */}
              <div className="mt-6">
                <h4 className="text-sm font-semibold mb-3">👁️ Live Preview</h4>
                <div className="bg-gray-50 border rounded-lg p-6 flex items-center justify-center">
                  <div
                    style={{
                      backgroundColor: nodeStyle.backgroundColor,
                      color: nodeStyle.textColor,
                      borderColor: nodeStyle.borderColor,
                      borderWidth: `${nodeStyle.borderWidth}px`,
                      borderStyle: nodeStyle.borderStyle,
                      borderRadius: nodeStyle.shape === 'rounded' ? '8px' : nodeStyle.shape === 'oval' || nodeStyle.shape === 'circle' ? '50%' : '0',
                      boxShadow: nodeStyle.shadow.enabled ? `${nodeStyle.shadow.offsetX}px ${nodeStyle.shadow.offsetY}px ${nodeStyle.shadow.blur}px ${nodeStyle.shadow.color}` : 'none',
                      padding: '16px 24px',
                      minWidth: '150px',
                      textAlign: 'center',
                      fontWeight: 'bold'
                    }}
                  >
                    Sample Node
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { setNodeStyle(defaultNodeStyle); setConnectionStyle(defaultConnectionStyle); }}>
            Reset to Default
          </Button>
          <Button onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700">
            Save Style
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PathNavStyleConfigurator;
